import { AxiosRequestConfig } from "axios";
import {
  createHook,
  createStore,
  StoreActionApi,
} from "react-sweet-state";
import { axiosSecuredInstance } from "../../configurations/axiosConfig";

type StoreApi = StoreActionApi<State>;

export interface Translation {
  type: string;
  value: string;
  source1: string;
  source2: string;
  source3: string;
  source4: string;
  target1: string;
}

interface State {
  error: string | null;
  translations?: Array<Translation>;
  isLoading: boolean;
  // Lists kept per `${type}|${value}`. Deliberately separate from `translations` above,
  // which holds only one type/value set at a time and is replaced wholesale on each fetch.
  translationLists: Record<string, Array<Translation>>;
}

const initialState: State = {
  error: null,
  translations: [],
  isLoading: false,
  translationLists: {},
};

const getTranslationListKey = (translationType: string, translationValue: string) =>
  `${translationType}|${translationValue}`;

// Keys currently being fetched. Several jackets can ask for the same list in the same
// render, and this keeps that to a single request.
const inFlightTranslationLists = new Set<string>();

const fetchTranslations = (url: string, action: (param: Array<any>) => ({ setState }: StoreApi) => any, config?: AxiosRequestConfig) =>
  async ({ dispatch }: StoreApi) => {
    try {
      const { data } = await axiosSecuredInstance.get(url, config);
      dispatch(action(data));
      return data;
    } catch (error: any) {
      dispatch(setError(error));
    }
  };

const setError = (error: string | null) => ({ setState }: StoreApi) => {
  setState({ error });
};

const hasAny = (data: Array<any>) => !data || data.length === 0;

// The translation endpoints serialize in PascalCase, unlike most of the API.
const mapTranslation = (type: any): Translation => ({
  type: type.TranslationType,
  value: type.TranslationValue,
  source1: type.Source1,
  source2: type.Source2,
  source3: type.Source3,
  source4: type.Source4,
  target1: type.Target1,
});

const setTranslationList = (transList: Array<any>) => ({ setState }: StoreApi) => {
  if (hasAny(transList)) {
    setState({ isLoading: false, translations: [] });
    return;
  }

  setState({ isLoading: false, translations: transList.map(mapTranslation) });
};

const Store = createStore<State, any>({
  initialState: { ...initialState },
  actions: {
    getTranslations:
      (translationType?: string, translationValue?: string) =>
        async ({ dispatch, getState }: StoreApi) => {
          try {
            const { translations } = getState();
            if (translations && translations.length > 0) {
              const matchingTranslation = translations.find(
                (t) => t.type === translationType && t.value === translationValue
              );
              if (matchingTranslation) return;
            }
            return dispatch(
              fetchTranslations(
                `/lookup/GetTranslationList?translationType=${translationType}&translationValue=${translationValue}`,
                setTranslationList
              )
            );
          } catch (error: any) {
            dispatch(setError(error));
          }
        },

    // Fetches one translation list and caches it under its own key, leaving the legacy
    // `translations` state above untouched.
    getTranslationList:
      (translationType: string, translationValue: string) =>
        async ({ setState, getState }: StoreApi) => {
          const key = getTranslationListKey(translationType, translationValue);

          if (getState().translationLists[key] || inFlightTranslationLists.has(key)) return;

          inFlightTranslationLists.add(key);
          try {
            const { data } = await axiosSecuredInstance.get(
              `/lookup/GetTranslationList?translationType=${encodeURIComponent(
                translationType
              )}&translationValue=${encodeURIComponent(translationValue)}`
            );

            setState({
              translationLists: {
                ...getState().translationLists,
                [key]: (data || []).map(mapTranslation),
              },
            });
          } catch (error: any) {
            setState({ error });
          } finally {
            inFlightTranslationLists.delete(key);
          }
        },
  },
  name: "translation",
});


const hook = createHook(Store);
export const useTranslation = () => {
  return hook();
};

const translationListsHook = createHook(Store, {
  selector: (state: State) => state.translationLists,
});

// Actions plus the keyed lists only, so consumers don't re-render when the legacy
// `translations` array changes.
export const useTranslationLists = () => translationListsHook();

export const selectTranslationList = (
  translationLists: Record<string, Array<Translation>>,
  translationType: string,
  translationValue: string
) => translationLists[getTranslationListKey(translationType, translationValue)];
